import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EndUserWithId } from '@app/core/interfaces/c5/end-user';
import { LocationGroup } from '@app/core/interfaces/c5/location-group';
import { UserLevel } from '@app/core/models/user-level';
import { EnhancedEndUser } from '@app/shared/models/enhanced-end-user';
import { Utils } from '@app/shared/services/utils.service';
import { wrapError } from '@app/utils/rxjs';
import { enhanceUsersWithExtension } from '@app/utils/user';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class LocationGroupRepository {
  constructor(
    private http: HttpClient,
    private utils: Utils
  ) {}

  assignUser(locationGroupId: number | string, userLoginName: string): Observable<void> {
    return this.utils.apiUrl(`/users/${userLoginName}/locationGroup/${locationGroupId}`).pipe(
      mergeMap((url) => this.http.put<void>(url, { loginName: userLoginName })),
      wrapError('location-group-repository:assign-user')
    );
  }

  add(siteName: number | string, locationGroup: Partial<LocationGroup>): Observable<void> {
    return this.utils.apiUrl(`/sites/${siteName}/locationGroups`).pipe(
      mergeMap((url) => this.http.post<void>(url, locationGroup)),
      wrapError('location-group-repository:add')
    );
  }

  delete(siteName: number | string, id: number | string): Observable<void> {
    return this.utils.apiUrl(`/sites/${siteName}/locationGroups/${id}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('location-group-repository:delete')
    );
  }

  get(siteName: number | string, id: number | string): Observable<LocationGroup> {
    return this.utils.apiUrl(`/sites/${siteName}/locationGroups/${id}`).pipe(
      mergeMap((url) => this.http.get<LocationGroup>(url)),
      wrapError('location-group-repository:get')
    );
  }

  listForSite(siteName: number | string): Observable<LocationGroup[]> {
    return this.utils.routeBasedApiUrl(`/sites/${siteName}/locationGroups`, UserLevel.EnterpriseAdmin).pipe(
      mergeMap((url) => this.http.get<LocationGroup[]>(url)),
      wrapError('location groups: by site'),
      wrapError('location-group-repository:list-for-site')
    );
  }

  listUsers(siteName: number | string, id: number | string): Observable<EnhancedEndUser[]> {
    return this.utils.apiUrl(`/sites/${siteName}/locationGroups/${id}/users`).pipe(
      mergeMap((url) => this.http.get<EndUserWithId[]>(url)),
      enhanceUsersWithExtension(),
      wrapError('location-group-repository:list-users')
    );
  }

  update(siteName: number | string, locationGroup: LocationGroup): Observable<void> {
    return this.utils.apiUrl(`/sites/${siteName}/locationGroups/${locationGroup.id}`).pipe(
      mergeMap((url) => this.http.put<void>(url, locationGroup)),
      wrapError('location-group-repository:update')
    );
  }
}
